import { useMemo } from "react";

import { AIRLINE_NAME_MAP } from "@/constants/airline";
import airportTags from "@/constants/airportTags";
import { PROGRAM_NAME_MAP } from "@/constants/programs";
import { lastTimeOptions } from "@/pages/Routes/components/FilterBar/utils/constants";

import { FilterOptions, FilterSourceData, OptionItem } from "../typing";
import { getBanksByProgram } from "../utils";
import { CabinsMap, UnknownAirlineKey } from "../utils/constants";

const transformerOptions = (map: Map<string, string>): OptionItem[] => {
  const options = [];
  for (const [value, label] of map.entries()) {
    options.push({ label, value });
  }

  return options;
};

const useProgramsOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const programsOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const programCode = list[index].program;
      programsOptionsMap.set(programCode, PROGRAM_NAME_MAP.get(programCode) || "");
    }
    return transformerOptions(programsOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useBanksOptions = (options: OptionItem[]): OptionItem[] => {
  return useMemo(() => {
    const banksOptionsMap = new Map<string, string>();
    for (let index = 0; index < options.length; index++) {
      const programCode = options[index].value;

      getBanksByProgram(programCode).forEach((bank) => {
        banksOptionsMap.set(bank, bank);
      });
    }

    return transformerOptions(banksOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);
};

const useTaxRange = (list: FilterSourceData): [number, number] | [] => {
  return useMemo(() => {
    const data = list.map((item) => item?.tax || 0).sort((a, b) => a - b);

    if (data && data.length > 0) {
      return [Number(data[0]), Number(data[data.length - 1])];
    } else {
      return [];
    }
  }, [list]);
};

const useCategoryOptions = (list: FilterSourceData): Array<OptionItem & { icon: any }> => {
  return useMemo(() => {
    const airportMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const airport = list[index].route.split("-");
      airportMap.set(airport[1], airport[1]);
    }

    return airportTags
      .filter((item) => {
        return item.airports.some((idx) => airportMap.get(idx));
      })
      .map((item) => ({ label: item.name, icon: item.icon, value: item.name }));
  }, [list]);
};

const useMaxPoints = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.price)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useAirlinesOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const airlinesOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const airlineNames = list[index].c_a;

      for (let index = 0; index < airlineNames.length; index++) {
        const airlineName = airlineNames[index] ?? "";
        airlinesOptionsMap.set(airlineName, airlineName);
      }

      if (!airlineNames.length) {
        airlinesOptionsMap.set(UnknownAirlineKey, UnknownAirlineKey);
      }
    }
    return transformerOptions(airlinesOptionsMap).sort((a, b) =>
      AIRLINE_NAME_MAP[a.label]?.localeCompare(AIRLINE_NAME_MAP[b.label])
    );
  }, [list]);
};

/**
 * 根据数据列表获取筛选项
 */
export const useFilterOptions = (list: FilterSourceData): FilterOptions => {
  const programsOptions = useProgramsOptions(list);
  const banksOptions = useBanksOptions(programsOptions);
  const taxRange = useTaxRange(list);
  const categoryOptions = useCategoryOptions(list);
  const maxPointsRange = useMaxPoints(list);
  const airlinesOptions = useAirlinesOptions(list);

  return {
    programsOptions,
    banksOptions,
    cabinsOptions: transformerOptions(CabinsMap),
    taxRange,
    categoryOptions,
    maxPointsRange,
    lastSeenRangeTime: lastTimeOptions,
    airlinesOptions,
  };
};
